<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import ProgressSpinner from 'primevue/progressspinner'
import AIBetaBadge from '@/components/AIBetaBadge.vue'
import CopyButton from '@/components/CopyButton.vue'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import FeedbackButtons from '@/components/FeedbackButtons.vue'
import WikiText from '@/components/wikitext/WikiText.vue'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { wikiToPlainText } from '@/util/tokenize_wiki_text'
import { translateGoalText } from './api'
import { TRANSLATION_EVENT_NAMES } from './event_names'
import { isRightToLeftLanguage, languageCodeFor } from './translation_languages'
import type { GoalTranslation, TranslationLanguage } from './translation_types'

type TextDirection = 'rtl' | 'ltr'

const props = withDefaults(defineProps<{
  goalTitle?: string
  goalText?: string
  goalUuid?: string
  isEdited?: boolean
  requestedLanguage?: string
  languages?: TranslationLanguage[]
}>(), {
  goalTitle: '',
  goalText: '',
  goalUuid: '',
  isEdited: false,
  requestedLanguage: '',
  languages: () => [],
})

const isOpen = defineModel<boolean>('open', { default: false })

const { profileId } = storeToRefs(useContentProfileStore())
const { betaVisibility } = storeToRefs(useVisibilityStore())

const translationLanguage = ref(props.requestedLanguage)
const translatedGoalTitle = ref('')
const translatedGoalText = ref('')
const isLoading = ref(false)
const hasLoadError = ref(false)
const translationContents = ref<HTMLElement | null>(null)

const translatedText = computed(() => `${translatedGoalTitle.value} ${translatedGoalText.value}`)

const analytics = getAnalyticsTracker().getChildContext({
  content_type: 'goal text',
  translation_language: translationLanguage,
  untranslated_text: computed(() => `${props.goalTitle} ${props.goalText}`),
})

const feedbackEventData = computed(() => ({
  goal_uuid: props.goalUuid,
  goal_title: props.goalTitle,
  translation_language: translationLanguage.value,
  translated_text: translatedText.value,
}))

const isRightToLeft = computed(() => isRightToLeftLanguage(props.languages, translationLanguage.value))
const translatedTextLang = computed(() => languageCodeFor(translationLanguage.value))
const textDirection = computed<TextDirection>(() => (isRightToLeft.value ? 'rtl' : 'ltr'))

function isMissingTranslationInputs() {
  const isMissingGoal = !props.goalText || !props.goalUuid || !props.goalTitle
  const isMissingLanguage = !translationLanguage.value
  return isMissingGoal || isMissingLanguage
}

async function focusTranslationContents() {
  await nextTick()
  translationContents.value?.focus()
}

function startLoading() {
  isLoading.value = true
  hasLoadError.value = false
  translatedGoalText.value = ''
  translatedGoalTitle.value = ''
}

// A newer request (another language, or edited goal text) supersedes any still in flight.
let latestRequest = 0

function isLatestRequest(request: number) {
  return request === latestRequest
}

function abandonPendingRequest() {
  latestRequest++
  isLoading.value = false
}

function requestTranslation() {
  return translateGoalText({
    goalUuid: props.goalUuid,
    goalText: props.goalText,
    isEdited: props.isEdited,
    language: translationLanguage.value,
    contentProfile: profileId.value,
    isBetaMode: betaVisibility.value,
  })
}

function showTranslation(translation: GoalTranslation) {
  translatedGoalText.value = translation.goal_text
  translatedGoalTitle.value = translation.goal_title
}

async function generateTranslation() {
  if (!isOpen.value) {
    return
  }
  if (isMissingTranslationInputs()) {
    console.warn('Cannot generate translation - missing required data')
    abandonPendingRequest()
    hasLoadError.value = true
    return
  }
  startLoading()
  analytics.sendEvent(TRANSLATION_EVENT_NAMES.INITIATED)
  const thisRequest = ++latestRequest
  try {
    const translation = await requestTranslation()
    if (!isLatestRequest(thisRequest)) {
      return
    }
    showTranslation(translation)
    analytics.sendEvent(TRANSLATION_EVENT_NAMES.TRANSLATED, { translated_text: translatedText.value })
  } catch (error) {
    if (!isLatestRequest(thisRequest)) {
      return
    }
    console.error('error generating translation: ', error)
    hasLoadError.value = true
  }
  isLoading.value = false
  await focusTranslationContents()
}

function closeTranslation() {
  analytics.sendEvent(TRANSLATION_EVENT_NAMES.DISMISSED)
  abandonPendingRequest()
  isOpen.value = false
  translationLanguage.value = ''
}

async function handleGoalTextChange(goalText: string) {
  if (!isOpen.value) {
    return
  }
  if (goalText) {
    await generateTranslation()
    return
  }
  closeTranslation()
}

// Opening and a new language often arrive together, so they share one handler and one request.
// Reopening takes the requested language again, since dismissing the card cleared it.
async function handleOpenOrLanguageChange(
  [isNowOpen, language]: [boolean, string],
  [wasOpen, previousLanguage]: [boolean, string],
) {
  const isOpening = isNowOpen && !wasOpen
  const isLanguageChange = language !== previousLanguage
  if (!isOpening && !isLanguageChange) {
    return
  }
  translationLanguage.value = language
  if (isNowOpen && language) {
    await generateTranslation()
  }
}

watch(() => props.goalText, handleGoalTextChange)
watch([isOpen, () => props.requestedLanguage], handleOpenOrLanguageChange)

onMounted(async () => {
  if (isOpen.value && translationLanguage.value) {
    await generateTranslation()
  }
})
</script>

<template>
  <div
    v-if="isOpen"
    ref="translationContents"
    class="mt-5 rounded-xs bg-gradient-to-r from-ai-bluePinkStart from-50% to-ai-bluePinkStop p-0.5 focus:outline-none"
    tabindex="-1"
    aria-live="polite"
    aria-atomic="true"
    aria-relevant="additions text"
    data-testid="translation-container"
  >
    <div class="relative rounded-xxs bg-white px-6 py-4">
      <button
        type="button"
        class="absolute right-4 top-3.5 rounded-full p-0.5 leading-none text-gray-900 hover:bg-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
        aria-label="close translation pop-up"
        data-testid="close-translation"
        @click="closeTranslation"
      >
        <span class="material-icons-round text-xl" aria-hidden="true">close</span>
      </button>
      <div v-if="isLoading" class="text-center" data-testid="translation-loading">
        <ProgressSpinner stroke-width="4" class="h-7 w-7 stroke-blue-400" />
        <p role="status" aria-busy="true" class="mx-auto text-md">Translating...</p>
      </div>
      <div v-else-if="hasLoadError" data-testid="translation-error">
        <p
          class="mb-0 inline-flex items-center rounded-xs border border-blue-300 bg-blue-200 p-2 text-md text-gray-900"
          role="alert"
        >
          <span class="material-icons-round pr-2 text-lg" aria-hidden="true">info_outline</span>We're not able to translate goals right now. Try again later.
        </p>
      </div>
      <div v-else data-testid="translation-result">
        <CopyTrackWrapper type="goal_translation" :title="translatedGoalTitle" gate-copy>
          <p role="status" class="sr-only">Goal translation completed.</p>
          <h4
            class="my-2.5 text-text-2xl font-medium leading-[1.1]"
            :class="{ 'block pr-6': isRightToLeft, 'inline-block': !isRightToLeft }"
            :dir="textDirection"
            data-testid="translated-title"
          >
            <span :lang="translatedTextLang">{{ translatedGoalTitle }}</span>
            <CopyButton
              :string-to-copy="wikiToPlainText(translatedGoalText)"
              button-text=""
              button-theme="grayTertiary"
              size="xs"
              aria-label="Copy translated goal"
              tooltip-text="Copied Translated Goal"
              :auto-hide-tooltip="true"
            >
              <template #icon>
                <span class="material-icons-outlined text-lg" aria-hidden="true">content_copy</span>
              </template>
            </CopyButton>
          </h4>
          <WikiText
            :text="translatedGoalText"
            :dir="textDirection"
            :lang="translatedTextLang"
            :class="{ 'text-right': isRightToLeft }"
            data-testid="translated-text"
          />
        </CopyTrackWrapper>
        <div class="flex gap-x-3 pt-2 leading-5">
          <AIBetaBadge />
          <FeedbackButtons :event-data="feedbackEventData" />
        </div>
      </div>
    </div>
  </div>
</template>
